import validations from "constants/validations";

import { Stack, TextField } from "@mui/material";
import DialogContentText from "@mui/material/DialogContentText";
import { changeUserEmail } from "API/calls";
import CodeField from "components/elements/CodeField";
import Modal from "components/elements/Modal";
import { useUserDataContext } from "context/UserDataContext";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { IChangeUserEmail } from "types/types";
import * as yup from "yup";

interface Props {
  onClose: () => void;
}

const ModalChangeUserEmail = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { mfaEnabled } = useUserDataContext();

  const handleChange = async (values: IChangeUserEmail) => {
    try {
      await changeUserEmail(values);
      enqueueSnackbar(t("settingsPage.changeUserEmail.modal.changeSuccess"), { variant: "success" });
      onClose();
    } catch (error: any) {
      const statusCode = error?.response?.status;

      if (statusCode === 400) {
        enqueueSnackbar(t("settingsPage.changeUserEmail.modal.otpNotValid"), { variant: "error" });
      } else if (statusCode === 403) {
        enqueueSnackbar(t("settingsPage.changeUserEmail.modal.tokenExpired"), { variant: "error" });
      } else if (statusCode === 409) {
        enqueueSnackbar(t("settingsPage.changeUserEmail.modal.emailAlreadyExists"), { variant: "error" });
      } else if (statusCode === 428) {
        enqueueSnackbar(t("settingsPage.changeUserEmail.modal.emailAlreadyVerified"), { variant: "warning" });
      } else if (statusCode === 429) {
        enqueueSnackbar(t("settingsPage.changeUserEmail.modal.emailChangeTokenStillActive"), { variant: "warning" });
      }
    }
  };

  const formik = useFormik({
    initialValues: { email: "", otp: "" },
    validationSchema: yup.object().shape({
      email: yup.string().required(t("fieldRequired")).matches(validations.email, t("fieldErrors.email")),
      otp: mfaEnabled
        ? yup
            .string()
            .required(t("fieldRequired"))
            .max(6, t("fieldMaxLength", { max: 6 }))
        : yup.string().notRequired(),
    }),
    onSubmit: async (values) => {
      handleChange(values);
    },
  });

  return (
    <Modal
      open
      onCancel={onClose}
      title={t("settingsPage.changeUserEmail.modal.title")}
      onConfirm={formik.handleSubmit}
      withCloseButton
      disableCloseOnClickOutside
      confirmTitle={t("settingsPage.changeUserEmail.modal.confirm")}>
      <DialogContentText sx={{ mb: 2 }}>{t("settingsPage.changeUserEmail.modal.subtitle")}</DialogContentText>
      <Stack spacing={2}>
        <TextField
          fullWidth={true}
          id={"email"}
          name={"email"}
          type={"email"}
          inputProps={{ autoCapitalize: "none" }}
          label={t("settingsPage.changeUserEmail.modal.newEmail")}
          value={formik.values.email ?? ""}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
        {mfaEnabled ? (
          <CodeField
            fullWidth={true}
            maxLength={6}
            id={"otp"}
            name={"otp"}
            label={t("twoFactorAuth.enableGoogleAuth.label")}
            value={formik.values.otp ?? ""}
            error={formik.touched.otp && Boolean(formik.errors.otp)}
            helperText={(formik.touched.otp && formik.errors.otp) ?? t("twoFactorAuth.enableGoogleAuth.helperText")}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        ) : null}
      </Stack>
    </Modal>
  );
};

export default ModalChangeUserEmail;
